import React, { useState } from "react";
import { Field, useFormikContext } from "formik";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import Fade from "@mui/material/Fade";
import { FormControlLabel, Radio } from "@mui/material";
import { RadioGroup } from "formik-mui";
import propTypes from "prop-types";

import SelectMenu from "../MaterialComponents/SelectMenu";
import MoreInfo from "../MaterialComponents/MoreInfo";

export const ScreeningSurvey = ({ questionTypes = {} } = {}) => {
  const { values, handleChange, errors, touched, isSubmitting } = useFormikContext();
  const [mySurveyMounted, setMySurveyMounted] = useState(false);
  const [lumenSurveyMounted, setLumenSurveyMounted] = useState(false);

  return (
    <Grid container spacing={3}>
      <Grid container wrap="nowrap" item xs={12}>
        <Typography variant="h6" component={"span"}>
          Demographic/screening survey
        </Typography>
        <MoreInfo
          outerContainerClassName="alignToTop"
          popup="Survey at the start of your study which categorises or excludes respondents based on their question responses"
        />
      </Grid>

      <Grid item xs={12}>
        <Field
          value={values.demographic_survey_or_lumen_questions}
          component={RadioGroup}
          name="demographic_survey_or_lumen_questions"
        >
          <FormControlLabel
            checked={values.demographic_survey_or_lumen_questions === "0"}
            value={0}
            control={<Radio disabled={isSubmitting} color="primary" />}
            label="I would not like to include a screening survey."
            disabled={isSubmitting}
          />
          <FormControlLabel
            checked={values.demographic_survey_or_lumen_questions === "1"}
            value={1}
            control={<Radio disabled={isSubmitting} color="primary" />}
            label="I would like to use my own pre-study survey"
            disabled={isSubmitting}
          />
          <FormControlLabel
            checked={values.demographic_survey_or_lumen_questions === "2"}
            value={2}
            control={<Radio disabled={isSubmitting} color="primary" />}
            label={`I would like to use one of Lumen's surveys`}
            disabled={isSubmitting}
          />
          <FormHelperText error>
            {touched["demographic_survey_or_lumen_questions"] &&
            !!errors["demographic_survey_or_lumen_questions"]
              ? errors["demographic_survey_or_lumen_questions"]
              : ""}
          </FormHelperText>
        </Field>
      </Grid>

      <Fade
        onEnter={() => setMySurveyMounted(true)}
        onExited={() => setMySurveyMounted(false)}
        mountOnEnter
        unmountOnExit
        in={values.demographic_survey_or_lumen_questions === "1" && lumenSurveyMounted === false}
      >
        <Grid item xs={12}>
          <Typography variant="subtitle1" component={"span"}>
            Please enter your custom survey address details
          </Typography>
        </Grid>
      </Fade>

      <Fade
        onEnter={() => setMySurveyMounted(true)}
        onExited={() => setMySurveyMounted(false)}
        mountOnEnter
        unmountOnExit
        in={values.demographic_survey_or_lumen_questions === "1" && lumenSurveyMounted === false}
      >
        <Grid container wrap="nowrap" item xs={12}>
          <TextField
            fullWidth
            name="demographic_survey_url"
            label="Demographic survey URL"
            variant="filled"
            value={values.demographic_survey_url}
            onChange={handleChange}
            error={touched["demographic_survey_url"] && !!errors["demographic_survey_url"]}
            helperText={
              touched["demographic_survey_url"] && !!errors["demographic_survey_url"]
                ? errors["demographic_survey_url"]
                : ""
            }
          />
          <MoreInfo outerContainerClassName="moreInfo" popup="The primary link to your demographic survey" />
        </Grid>
      </Fade>

      <Fade
        mountOnEnter
        unmountOnExit
        in={values.demographic_survey_or_lumen_questions === "1" && lumenSurveyMounted === false}
      >
        <Grid container wrap="nowrap" item xs={12}>
          <TextField
            fullWidth
            name="demographic_survey_insert_parameter"
            label="Demographic survey insert parameter"
            variant="filled"
            value={values.demographic_survey_insert_parameter}
            onChange={handleChange}
            error={
              touched["demographic_survey_insert_parameter"] &&
              !!errors["demographic_survey_insert_parameter"]
            }
            helperText={
              touched["demographic_survey_insert_parameter"] &&
              !!errors["demographic_survey_insert_parameter"]
                ? errors["demographic_survey_insert_parameter"]
                : ""
            }
          />
          <MoreInfo outerContainerClassName="moreInfo" popup="The query string field for your respondents ID" />
        </Grid>
      </Fade>

      <Fade
        onEnter={() => setLumenSurveyMounted(true)}
        onExited={() => setLumenSurveyMounted(false)}
        mountOnEnter
        unmountOnExit
        in={values.demographic_survey_or_lumen_questions === "2" && mySurveyMounted === false}
      >
        <Grid item xs={12}>
          <Typography variant="subtitle1" component={"span"}>
            Please choose the question set you would like to use
          </Typography>
        </Grid>
      </Fade>

      <Fade
        onEnter={() => setLumenSurveyMounted(true)}
        onExited={() => setLumenSurveyMounted(false)}
        mountOnEnter
        unmountOnExit
        in={values.demographic_survey_or_lumen_questions === "2" && mySurveyMounted === false}
      >
        <Grid container wrap="nowrap" item xs={12}>
          <SelectMenu
            name="pre_assign_question_group"
            label="Pre-assign question groups"
            options={questionTypes|| {}}
          />
          <MoreInfo
            outerContainerClassName="moreInfo"
            popup="You can select from a set of pre-defined questions"
          />
        </Grid>
      </Fade>
    </Grid>
  );
};

ScreeningSurvey.propTypes = {
  questionTypes: propTypes.object,
};
